/**
 * ConfigPanel Component
 *
 * Step 2 of Parlay Workshop - Configure parlay settings
 * Leg count, sports selection, prop types
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { getActiveSports } from '../../services/predictions';

const LEG_OPTIONS = [2, 3, 4, 5, 6];

// Every sport this page knows how to build a parlay for. Which of these is
// actually OFFERED is decided at runtime by /predictions/active-sports, because
// this list alone cannot know what is in season -- it kept showing NBA and NHL
// for months after both seasons ended, and defaulted the selection to NBA.
//
// A sport must appear here AND be in season to be selectable. Being in season is
// not sufficient: NCAA_F is in season but has tracks_player_stats = false, so it
// has no player props to build a parlay from and no PROP_TYPES entry below.
const SUPPORTED_SPORTS = [
  { id: 'mlb', emoji: '⚾', name: 'MLB' },
  { id: 'nba', emoji: '🏀', name: 'NBA' },
  { id: 'nfl', emoji: '🏈', name: 'NFL' },
  { id: 'nhl', emoji: '🏒', name: 'NHL' }
];

// Sport-specific prop types
const PROP_TYPES_BY_SPORT = {
  mlb: [
    { id: 'hits', label: 'Hits', emoji: '🎯' },
    { id: 'home_runs', label: 'Home Runs', emoji: '⚾' },
    { id: 'total_bases', label: 'Total Bases', emoji: '💥' },
    { id: 'rbis', label: 'RBIs', emoji: '🏆' },
    { id: 'runs', label: 'Runs', emoji: '🏃' },
    { id: 'pitcher_strikeouts', label: 'Pitcher Ks', emoji: '🎳' }
  ],
  nba: [
    { id: 'points', label: 'Points', emoji: '🎯' },
    { id: 'rebounds', label: 'Rebounds', emoji: '🏀' },
    { id: 'assists', label: 'Assists', emoji: '🎯' },
    { id: 'three_pm', label: '3-Pointers', emoji: '🏹' }
  ],
  nhl: [
    { id: 'goals', label: 'Goals', emoji: '🥅' },
    { id: 'assists', label: 'Assists', emoji: '🏒' },
    { id: 'points', label: 'Points', emoji: '🎯' },
    { id: 'shots', label: 'Shots on Goal', emoji: '🏒' }
  ],
  nfl: [
    { id: 'passing_yards', label: 'Passing Yards', emoji: '🏈' },
    { id: 'rushing_yards', label: 'Rushing Yards', emoji: '🏃' },
    { id: 'receiving_yards', label: 'Receiving Yards', emoji: '🙌' },
    { id: 'receptions', label: 'Receptions', emoji: '🎯' },
    { id: 'passing_touchdowns', label: 'Pass TDs', emoji: '🏆' },
    { id: 'rushing_touchdowns', label: 'Rush TDs', emoji: '🏆' },
    { id: 'receiving_touchdowns', label: 'Rec TDs', emoji: '🏆' }
  ]
};

// Get all unique prop types for selected sports
const getPropTypesForSports = (selectedSports) => {
  const propMap = new Map();
  selectedSports.forEach(sport => {
    const sportProps = PROP_TYPES_BY_SPORT[sport] || [];
    sportProps.forEach(prop => {
      if (!propMap.has(prop.id)) {
        propMap.set(prop.id, prop);
      }
    });
  });
  return Array.from(propMap.values());
};

const VIBE_LABELS = {
  safe: { emoji: '🐢', name: 'Safe' },
  smart: { emoji: '🎯', name: 'Smart' },
  hot: { emoji: '🔥', name: 'Hot' },
  surprise: { emoji: '🎲', name: 'Surprise' }
};

export default function ConfigPanel({
  vibe,
  initialConfig,
  onSubmit,
  onBack,
  isLoading
}) {
  const [legCount, setLegCount] = useState(initialConfig?.legCount || 3);
  const [activeSportIds, setActiveSportIds] = useState(null);   // null = still loading
  const [sportsError, setSportsError] = useState(null);
  const [selectedSports, setSelectedSports] = useState(initialConfig?.sports || []);

  useEffect(() => {
    let cancelled = false;
    getActiveSports()
      .then(sports => {
        if (cancelled) return;
        setActiveSportIds(sports.map(s => s.sport));
      })
      .catch(() => {
        if (cancelled) return;
        // Show nothing rather than a stale hardcoded list. Offering a sport that
        // is out of season produces an empty parlay and looks like a broken page.
        setActiveSportIds([]);
        setSportsError('Could not load which sports are in season.');
      });
    return () => { cancelled = true; };
  }, []);

  // Offer only sports this page supports AND that are currently in season.
  const SPORTS = useMemo(() => {
    if (activeSportIds === null) return [];
    return SUPPORTED_SPORTS.filter(s => activeSportIds.includes(s.id));
  }, [activeSportIds]);

  // Once the in-season list arrives, drop any preselected sport that is out of
  // season and default to the first available one.
  useEffect(() => {
    if (activeSportIds === null) return;
    setSelectedSports(prev => {
      const valid = prev.filter(id => activeSportIds.includes(id));
      if (valid.length > 0) return valid;
      const first = SPORTS[0]?.id;
      return first ? [first] : [];
    });
  }, [activeSportIds, SPORTS]);

  // Get available prop types based on selected sports
  const availablePropTypes = getPropTypesForSports(selectedSports);

  const [selectedBetTypes, setSelectedBetTypes] = useState([]);

  // Keep bet types in step with whatever sports are actually selected.
  useEffect(() => {
    const available = getPropTypesForSports(selectedSports).map(p => p.id);
    setSelectedBetTypes(prev => {
      const kept = prev.filter(id => available.includes(id));
      return kept.length > 0 ? kept : available.slice(0, 3);
    });
  }, [selectedSports]);

  const toggleSport = useCallback((sportId) => {
    setSelectedSports(prev => {
      let newSports;
      if (prev.includes(sportId)) {
        // Don't allow removing last sport
        if (prev.length === 1) return prev;
        newSports = prev.filter(s => s !== sportId);
      } else {
        newSports = [...prev, sportId];
      }

      // Update available bet types when sports change
      const newAvailable = getPropTypesForSports(newSports);
      const newAvailableIds = new Set(newAvailable.map(p => p.id));

      // Keep only bet types that are still available, add defaults if needed
      setSelectedBetTypes(currentTypes => {
        const validTypes = currentTypes.filter(t => newAvailableIds.has(t));
        if (validTypes.length === 0) {
          // Select first 3 available types
          return newAvailable.slice(0, 3).map(p => p.id);
        }
        return validTypes;
      });

      return newSports;
    });
  }, []);

  const toggleProp = useCallback((propId) => {
    setSelectedBetTypes(prev => {
      if (prev.includes(propId)) {
        // Don't allow removing last prop type
        if (prev.length === 1) return prev;
        return prev.filter(p => p !== propId);
      }
      return [...prev, propId];
    });
  }, []);

  const handleSubmit = useCallback(() => {
    onSubmit({
      legCount,
      sports: selectedSports,
      betTypes: selectedBetTypes
    });
  }, [legCount, selectedSports, selectedBetTypes, onSubmit]);

  const vibeInfo = VIBE_LABELS[vibe] || VIBE_LABELS.smart;

  return (
    <div className="glass-card p-6">
      {/* Vibe indicator */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-text-primary mb-1">
            Configure Your {vibeInfo.emoji} {vibeInfo.name} Parlay
          </h2>
          <p className="text-text-secondary text-sm">
            Customize how Knewton builds your parlay
          </p>
        </div>
        <button
          onClick={onBack}
          className="text-text-secondary hover:text-text-primary transition-colors"
        >
          ← Change vibe
        </button>
      </div>

      {/* Leg count */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-text-primary mb-3">
          How many legs?
        </label>
        <div className="flex gap-2">
          {LEG_OPTIONS.map(num => (
            <button
              key={num}
              onClick={() => setLegCount(num)}
              className={`
                w-12 h-12 rounded-lg font-bold text-lg transition-all
                ${legCount === num
                  ? 'bg-accent-blue text-white ring-2 ring-accent-blue/50'
                  : 'bg-bg-card text-text-secondary hover:bg-bg-card-hover hover:text-text-primary'
                }
              `}
            >
              {num === 6 ? '6+' : num}
            </button>
          ))}
        </div>
      </div>

      {/* Sports selection */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-text-primary mb-3">
          Which sports? <span className="text-text-tertiary font-normal">(select multiple)</span>
        </label>
        {activeSportIds === null && (
          <p className="text-sm text-text-tertiary">Checking which sports are in season…</p>
        )}
        {activeSportIds !== null && SPORTS.length === 0 && (
          <p className="text-sm text-text-tertiary">
            {sportsError || 'No supported sports are in season right now.'}
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          {SPORTS.map(sport => (
            <button
              key={sport.id}
              onClick={() => toggleSport(sport.id)}
              className={`
                px-4 py-3 rounded-lg flex items-center gap-2 transition-all
                ${selectedSports.includes(sport.id)
                  ? 'bg-accent-blue/20 text-accent-blue border border-accent-blue/40'
                  : 'bg-bg-card text-text-secondary hover:bg-bg-card-hover border border-transparent'
                }
              `}
            >
              <span>{sport.emoji}</span>
              <span className="font-medium">{sport.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Prop types - dynamic based on selected sports */}
      <div className="mb-8">
        <label className="block text-sm font-medium text-text-primary mb-3">
          Prop types to include:
          <span className="text-text-tertiary font-normal ml-2">
            (based on selected sports)
          </span>
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {availablePropTypes.map(prop => (
            <label
              key={prop.id}
              className={`
                flex items-center gap-2 p-3 rounded-lg cursor-pointer transition-all
                ${selectedBetTypes.includes(prop.id)
                  ? 'bg-accent-green/10 text-text-primary border border-accent-green/30'
                  : 'bg-bg-card text-text-secondary hover:bg-bg-card-hover border border-transparent'
                }
              `}
            >
              <input
                type="checkbox"
                checked={selectedBetTypes.includes(prop.id)}
                onChange={() => toggleProp(prop.id)}
                className="sr-only"
              />
              <span className={`
                w-5 h-5 rounded border-2 flex items-center justify-center text-xs
                ${selectedBetTypes.includes(prop.id)
                  ? 'bg-accent-green border-accent-green text-white'
                  : 'border-text-tertiary'
                }
              `}>
                {selectedBetTypes.includes(prop.id) && '✓'}
              </span>
              <span className="text-sm">{prop.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Submit button */}
      <button
        onClick={handleSubmit}
        disabled={isLoading || selectedSports.length === 0 || selectedBetTypes.length === 0}
        className={`
          w-full py-4 rounded-xl font-bold text-lg transition-all
          ${isLoading
            ? 'bg-accent-blue/50 text-white/70 cursor-wait'
            : 'bg-gradient-to-r from-accent-blue to-accent-purple text-white hover:shadow-lg hover:scale-[1.02]'
          }
          disabled:opacity-50 disabled:cursor-not-allowed
        `}
      >
        {isLoading ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
            Knewton is thinking...
          </span>
        ) : (
          '✨ BUILD MY PARLAY ✨'
        )}
      </button>

      {/* Summary */}
      <div className="mt-4 text-center text-sm text-text-secondary">
        {legCount} legs • {selectedSports.length} sport{selectedSports.length !== 1 ? 's' : ''} •{' '}
        {selectedBetTypes.length} prop type{selectedBetTypes.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
}
